'use client'

import {useParams} from 'next/navigation'
import {useEffect, useState} from 'react'

import {SlugGroup} from '@/app/[locale]/(docs)/components/DocsNavigation'
import ChevronToRight from '@/app/[locale]/(website)/components/icons/ChevronToRight'
import {Link} from '@/i18n/routing'

type DocsMenuProps = {
    groupedSlugs: SlugGroup
    translatedCategories: Record<string, string>
}

export default function DocsNavigationMenu({groupedSlugs, translatedCategories}: DocsMenuProps) {
    const params = useParams()
    const activeSlug = params?.slug as string
    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})

    useEffect(() => {
        const updatedOpenCategories = Object.entries(groupedSlugs).reduce(
            (acc, [category, items]) => {
                const isCategoryActive = items.some(({slug}) => slug === activeSlug)
                acc[category] = isCategoryActive
                return acc
            },
            {} as Record<string, boolean>,
        )
        setOpenCategories(updatedOpenCategories)
    }, [activeSlug, groupedSlugs])

    return (
        <ul className="flex flex-col lg:py-4 w-full">
            {Object.entries(groupedSlugs).map(([category, items]) => {
                const isCategoryActive = openCategories[category] || false

                return (
                    <li key={category} className="lg:mx-4">
                        <details
                            name="menu"
                            className="text-text-quaternary"
                            open={isCategoryActive}
                        >
                            <summary
                                className={`text-sm h-8 px-2 flex items-center hover:bg-bg-tertiary rounded-md ${
                                    isCategoryActive ? 'text-text-primary' : ''
                                }`}
                                aria-controls={category}
                            >
                                {translatedCategories[category]}
                                <ChevronToRight className="size-4 ml-auto" />
                            </summary>
                            <ul id={category} className="py-1 pl-2" role="group">
                                {items.map(({slug, title}) => {
                                    const isActive = slug === activeSlug
                                    return (
                                        <li key={slug}>
                                            <Link
                                                href={{
                                                    pathname: '/docs/[slug]',
                                                    params: {slug: slug},
                                                }}
                                                className={`text-sm h-8 px-2 mb-px flex items-center hover:bg-bg-tertiary rounded-md ${
                                                    isActive
                                                        ? 'bg-bg-tertiary text-text-primary'
                                                        : ''
                                                }`}
                                                aria-current={isActive ? 'page' : undefined}
                                            >
                                                {title}
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>
                        </details>
                    </li>
                )
            })}
        </ul>
    )
}
